import {
  Box,
  Avatar,
  Flex,
  Heading,
  Text,
  Image,
  Spacer,
  Badge,
  Icon,
  Stack,
  Tag,
  Tooltip,
  Spinner,
  useColorModeValue
} from '@chakra-ui/react';
import {
  GiThorHammer,
  GiMaterialsScience
} from 'react-icons/gi';
import { BiRuler } from 'react-icons/bi';
import { FaRegClone } from 'react-icons/fa';
import { BsTrashFill } from 'react-icons/bs';
import { ItemModel } from 'models';

export interface BasicItemCardTag {
  label: string;
  colorScheme?: string;
  textStyle?: {
    fontWeight?: string;
    color?: string;
  };
  variant?: 'subtle' | 'solid' | 'outline';
}

export interface BasicItemCardProps {
  item: ItemModel;
  onClick?: () => void;
  onArtistClick?: () => void;
  onItemClick?: () => void;
  useAvatar?: boolean;
  selected?: boolean;
  showMetadata?: boolean;
  tag?: BasicItemCardTag;
  onRemove?: () => void;
  removeLabel?: string;
  removeDisabled?: boolean;
  isRemoving?: boolean;
}

export enum BasicItemCardSizingConfig {
  CARD_HEIGHT = '80px',
  PANEL_MIN_HEIGHT = '80px'
}

export enum BasicItemCardStylingConfig {
  BORDER_COLOR_LIGHT = '#f1f3f7',
  BORDER_COLOR_DARK = '#3f434c'
}

export default function BasicItemCard({
  item,
  onClick,
  onArtistClick,
  onItemClick,
  useAvatar = false,
  selected = false,
  showMetadata = false,
  tag,
  onRemove,
  removeLabel = 'Remove Item',
  removeDisabled = false,
  isRemoving = false,
}: BasicItemCardProps) {
  const boxColor  = useColorModeValue('gray.200', 'gray.800');

  function renderItemImage() {
    if (useAvatar) {
      return <Avatar backgroundColor={'white'} marginRight={4} name={item.name} src={item.image} />
    } else {
      return <Image fit={'contain'} marginRight={4} src={item.image} alt={item.name} width={BasicItemCardSizingConfig.CARD_HEIGHT} height={BasicItemCardSizingConfig.CARD_HEIGHT} />;
    }
  }

  function renderItemSizingInfo() {
    if (item.hasDimensions()) {
      return (
        <Flex direction={'row'} gap={1} alignItems="center">
          <Icon as={BiRuler} fontSize="xs" />
          <Text fontSize={'xs'}>{item.getDimensionsForDisplay()}</Text>
        </Flex>
      );
    }
    return null;
  }

  function renderItemMaterialInfo() {
    if (item.hasMaterial()) {
      return (
        <Flex direction="row" gap={1} alignItems="center">
          <Icon as={GiMaterialsScience} fontSize="xs" />
          <Text fontSize="xs">{item.getMaterialForDisplay()}</Text>
        </Flex>
      );
    }
    return null;
  }

  function renderItemEditionInfo() {
    if (item.hasEditionSize()) {
      return (
        <Flex direction="row" gap={1} alignItems="center">
          <Icon as={FaRegClone} fontSize="xs" />
          <Text fontSize="xs">{item.getEditionSize()}</Text>
        </Flex>
      );
    }
    return null;
  }

  function renderMetadata() {
    if (!showMetadata) {
      return null;
    }

    const metadata = [
      renderItemSizingInfo(),
      renderItemMaterialInfo(),
      renderItemEditionInfo(),
    ].filter(Boolean);

    if (!metadata.length) {
      return null;
    }

    return (
      <Stack spacing={1} marginTop={1}>
        {metadata}
      </Stack>
    );
  }

  function renderPricingData() {
    if (item.hasMarketSummaryData()) {
      return (
        <Flex direction="column" gap={2} alignItems="flex-end">
          <Heading fontSize="sm">{item.getEstimatedMarketPriceForDisplay()}</Heading>
          <Flex direction={'row'} alignItems={'center'} gap={1}>
            <Text fontSize={'xs'}>{item.getSoldListingsQuantity()}</Text>
            <Flex
              padding={1}
              align={'center'}
              justify={'center'}
              rounded={'full'}
              border="1px">
              <Icon as={GiThorHammer} w={3} h={3} />
            </Flex>
          </Flex>
        </Flex>
      );
    } else if (item.hasReleasePriceData()) {
      return (
        <Flex direction="column" alignItems="flex-end" width={'100%'} gap={2}>
          <Flex direction={'column'}>
            <Text fontSize={'xs'}>{item.getReleaseConvertedPriceForDisplay()}</Text>
            <Heading fontSize={'10px'}>Retail Price</Heading>
          </Flex>
          <Badge colorScheme='pink'>
            No Sales
          </Badge>
        </Flex>
      );
    }

    return null;
  }

  const handleItemClick = (e: React.MouseEvent) => {
    if (onItemClick) {
      e.stopPropagation();
      onItemClick();
    }
  };

  const handleArtistClick = (e: React.MouseEvent) => {
    if (onArtistClick) {
      e.stopPropagation();
      onArtistClick();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    if (onRemove && !removeDisabled && !isRemoving) {
      e.stopPropagation();
      onRemove();
    }
  };

  function renderTag() {
    if (!tag) {
      return null;
    }

    return (
      <Tag size="sm" colorScheme={tag.colorScheme || 'gray'} variant={tag.variant || 'subtle'}>
        <Flex direction="row" align="center" gap={1}>
          <Text>{tag.label.split(':')[0]}:</Text>
          <Text fontWeight={tag.textStyle?.fontWeight} color={tag.textStyle?.color}>
            {tag.label.split(':')[1]?.trim() || tag.label}
          </Text>
        </Flex>
      </Tag>
    );
  }

  function renderRemoveButton() {
    if (!onRemove) {
      return null;
    }

    return (
      <Tooltip label={removeLabel}>
        <Flex
          as="button"
          w={6}
          h={6}
          align="center"
          justify="center"
          rounded="full"
          border="1px"
          borderColor={removeDisabled ? 'gray.300' : 'red.500'}
          cursor={removeDisabled ? 'not-allowed' : 'pointer'}
          opacity={removeDisabled ? 0.4 : 1}
          onClick={handleRemoveClick}
        >
          {isRemoving ? (
            <Spinner size="xs" color="red.500" />
          ) : (
            <Icon as={BsTrashFill} color="red.500" />
          )}
        </Flex>
      </Tooltip>
    );
  }

  const tagElement = renderTag();
  const pricingElement = renderPricingData();
  const removeElement = renderRemoveButton();
  const hasRightColumn = tagElement || pricingElement || removeElement;

  return (
    <Box
      minHeight={BasicItemCardSizingConfig.CARD_HEIGHT}
      width="100%"
      overflow='hidden'
      onClick={onClick}
      cursor={onClick ? 'pointer' : ''}
      padding={2}
      border="1px solid"
      borderColor={useColorModeValue(BasicItemCardStylingConfig.BORDER_COLOR_LIGHT, BasicItemCardStylingConfig.BORDER_COLOR_DARK)}
      backgroundColor={selected ? boxColor : ''}
      _hover={onClick ? { 'backgroundColor': boxColor } : {}}
    >
      <Flex direction="row" alignItems="center" justifyContent="flex-start">
        {renderItemImage()}
        <Flex direction="column" textAlign="left" gap={1} paddingRight={4}>
          <Heading
            fontSize="sm"
            onClick={handleItemClick}
            cursor={onItemClick ? 'pointer' : ''}
            _hover={onItemClick ? { color: 'blue.500' } : {}}
          >
            {item.getDisplayName()}
          </Heading>
          <Text
            fontSize="xs"
            onClick={handleArtistClick}
            cursor={onArtistClick ? 'pointer' : ''}
            _hover={onArtistClick ? { color: 'blue.500' } : {}}
          >
            {item.getArtistName()}
          </Text>
          {renderMetadata()}
        </Flex>
        <Spacer />
        {hasRightColumn && (
          <Stack spacing={2} align="flex-end">
            {tagElement}
            {pricingElement}
            {removeElement}
          </Stack>
        )}
      </Flex>
    </Box>
  );
}
